import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { IMAGES } from "@/lib/images";
import { Button } from "@/components/ui/button";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";
import {
  Sparkles, Leaf, Brush, ArrowRight, Star, ShieldCheck,
  Clock, Recycle, ThumbsUp, Quote,
} from "lucide-react";

const Overline = ({ children }) => (
  <span className="text-xs font-semibold uppercase tracking-[0.22em] text-[#8FA38C]">{children}</span>
);

export default function Home() {
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [gallery, setGallery] = useState([]);

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
    api.get("/testimonials").then((r) => setTestimonials(r.data)).catch(() => {});
    api.get("/gallery").then((r) => setGallery(r.data)).catch(() => {});
  }, []);

  const featured = gallery[0];

  return (
    <div className="bg-[#FAFAFA]">
      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-center" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src={IMAGES.heroLandscape} alt="Pristine landscaped garden" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0E1F18]/85 via-[#0E1F18]/55 to-transparent" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full py-28">
          <div className="max-w-2xl animate-fade-up">
            <Overline>Cleaning & Landscaping · Calgary · Airdrie · Okotoks</Overline>
            <h1 className="mt-5 font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold text-white leading-[1.05] tracking-tight text-balance">
              A master's touch for your home & garden
            </h1>
            <p className="mt-6 text-lg text-white/85 font-light leading-relaxed max-w-xl">
              Spotless interiors and breathtaking outdoor spaces, delivered by craftspeople who treat every property like their own.
            </p>
            <div className="mt-9 flex flex-wrap gap-4">
              <Button
                onClick={() => navigate("/booking")}
                data-testid="hero-book-now-btn"
                className="bg-[#C47B57] hover:bg-[#D58C68] text-white rounded-full px-8 py-6 text-base font-medium"
              >
                Book a Service <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button
                onClick={() => navigate("/visualizer")}
                data-testid="hero-visualizer-btn"
                className="bg-white/10 hover:bg-white/20 text-white border border-white/30 backdrop-blur-md rounded-full px-8 py-6 text-base font-medium"
              >
                <Sparkles className="mr-2 h-4 w-4" /> Visualize Your Yard
              </Button>
            </div>
            <div className="mt-10 flex flex-wrap items-center gap-6 text-white/80 text-sm">
              <span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-[#8FA38C]" /> Licensed & Insured</span>
              <span className="flex items-center gap-2"><Star className="h-4 w-4 text-[#C47B57] fill-[#C47B57]" /> 4.9 · 800+ reviews</span>
              <span className="flex items-center gap-2"><Recycle className="h-4 w-4 text-[#8FA38C]" /> Eco-friendly</span>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES OVERVIEW */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28" data-testid="services-overview">
        <div className="max-w-2xl">
          <Overline>What we do</Overline>
          <h2 className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#1A362D] tracking-tight">
            Two crafts. One standard of excellence.
          </h2>
          <p className="mt-4 text-[#4A5D4E] text-lg font-light">
            From deep cleans to full garden transformations, we bring meticulous care to every corner — indoors and out.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-12">
          {[
            { icon: Brush, title: "Cleaning Services", desc: "Residential, commercial, deep cleaning and move-in/move-out — handled with detail-obsessed precision.", img: IMAGES.cleanLivingRoom, cat: "cleaning" },
            { icon: Leaf, title: "Landscaping Services", desc: "Lawn care, garden design, hardscaping and recurring maintenance plans that keep your outdoors thriving.", img: IMAGES.landscapingModern, cat: "landscaping" },
          ].map((c) => (
            <div key={c.title} className="group relative overflow-hidden rounded-2xl bg-white border border-[#E2E8E4]/60 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_20px_40px_rgb(0,0,0,0.08)] transition-all duration-300">
              <div className="aspect-[16/10] overflow-hidden">
                <img src={c.img} alt={c.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
              </div>
              <div className="p-8">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[#F2F4F2] text-[#1A362D] mb-4">
                  <c.icon className="h-6 w-6" />
                </span>
                <h3 className="font-heading text-2xl font-semibold text-[#1A362D]">{c.title}</h3>
                <p className="mt-3 text-[#4A5D4E] font-light">{c.desc}</p>
                <Link to="/services" data-testid={`services-card-${c.cat}`} className="mt-5 inline-flex items-center gap-2 text-[#C47B57] font-medium hover:gap-3 transition-all">
                  Explore services <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          ))}
        </div>

        {services.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
            {services.slice(0, 4).map((s) => (
              <button
                key={s.service_id}
                onClick={() => navigate("/services")}
                className="text-left p-5 rounded-xl bg-[#F2F4F2] hover:bg-[#E9EDE9] transition-colors"
              >
                <p className="font-medium text-[#1A362D]">{s.name}</p>
                <p className="text-sm text-[#8A9A8C] mt-1">from ${s.price}</p>
              </button>
            ))}
          </div>
        )}
      </section>

      {/* AI VISUALIZER TEASER */}
      <section className="relative py-20 md:py-28" data-testid="visualizer-teaser" style={{ backgroundImage: `url(${IMAGES.earthyBg})`, backgroundSize: "cover" }}>
        <div className="absolute inset-0 bg-[#F2F4F2]/80" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <Overline>The Master Touch difference</Overline>
            <h2 className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#1A362D] tracking-tight">
              See your dream yard before we lift a shovel
            </h2>
            <p className="mt-4 text-[#4A5D4E] text-lg font-light">
              Upload a photo of your space and our AI Landscape Visualizer generates a photorealistic preview in your chosen style — modern, cottage, minimalist or tropical. Love it? Request that exact design in one click.
            </p>
            <ul className="mt-6 space-y-3">
              {["Instant photorealistic before/after", "Four curated design styles", "One-click quote request"].map((t) => (
                <li key={t} className="flex items-center gap-3 text-[#1A362D]">
                  <Sparkles className="h-4 w-4 text-[#C47B57]" /> {t}
                </li>
              ))}
            </ul>
            <Button onClick={() => navigate("/visualizer")} data-testid="teaser-visualizer-btn" className="mt-8 bg-[#1A362D] hover:bg-[#254A3E] text-white rounded-full px-8 py-6 text-base">
              Try the AI Visualizer <Sparkles className="ml-2 h-4 w-4" />
            </Button>
          </div>
          <div className="animate-fade-up">
            {featured ? (
              <BeforeAfterSlider before={featured.before} after={featured.after} />
            ) : (
              <img src={IMAGES.landscapingModern} alt="Landscape" className="rounded-xl w-full aspect-[4/3] object-cover" />
            )}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
        <div className="text-center max-w-2xl mx-auto">
          <Overline>Why Master Touch</Overline>
          <h2 className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#1A362D] tracking-tight">Built on trust & craftsmanship</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mt-12">
          {[
            { icon: ShieldCheck, t: "Licensed & Insured", d: "Fully bonded for total peace of mind." },
            { icon: Clock, t: "On-Time, Always", d: "Punctual crews that respect your schedule." },
            { icon: Recycle, t: "Eco-Friendly", d: "Family- and pet-safe products as standard." },
            { icon: ThumbsUp, t: "Satisfaction Guaranteed", d: "We make it right, every time." },
          ].map((f) => (
            <div key={f.t} className="p-8 rounded-xl bg-white border border-[#E2E8E4]/60 hover:-translate-y-1 transition-transform duration-300">
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[#1A362D] text-white mb-4">
                <f.icon className="h-6 w-6" />
              </span>
              <h3 className="font-heading text-xl font-semibold text-[#1A362D]">{f.t}</h3>
              <p className="mt-2 text-sm text-[#4A5D4E] font-light">{f.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="bg-[#1A362D] py-20 md:py-28" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <span className="text-xs font-semibold uppercase tracking-[0.22em] text-[#8FA38C]">Loved by our clients</span>
            <h2 className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold text-white tracking-tight">Real homes. Real results.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
            {testimonials.slice(0, 6).map((t, i) => (
              <div key={i} className="p-8 rounded-xl bg-white/5 border border-white/10 backdrop-blur-sm">
                <Quote className="h-7 w-7 text-[#C47B57]" />
                <p className="mt-4 text-white/90 font-light leading-relaxed">"{t.text}"</p>
                <div className="mt-5 flex items-center gap-1">
                  {Array.from({ length: t.rating }).map((_, k) => (
                    <Star key={k} className="h-4 w-4 text-[#C47B57] fill-[#C47B57]" />
                  ))}
                </div>
                <p className="mt-3 text-white font-medium">{t.name}</p>
                <p className="text-sm text-[#8FA38C]">{t.service} · {t.location}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
        <div className="rounded-3xl bg-white border border-[#E2E8E4]/60 shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-10 md:p-16 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#1A362D] tracking-tight max-w-2xl mx-auto">
            Ready for the Master Touch?
          </h2>
          <p className="mt-4 text-[#4A5D4E] text-lg font-light max-w-xl mx-auto">
            Book in minutes or get a free design preview. We'll handle the rest.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Button onClick={() => navigate("/booking")} className="bg-[#C47B57] hover:bg-[#D58C68] text-white rounded-full px-8 py-6 text-base">
              Book Now
            </Button>
            <Button onClick={() => navigate("/contact")} variant="outline" className="rounded-full px-8 py-6 text-base border-[#1A362D] text-[#1A362D] hover:bg-[#F2F4F2]">
              Get a Quote
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
